import { getNavigatorLanguageOrFallback } from "internationalization/i18n";
import { ReactNode, createContext } from "react";
import { ResolvedTheme, ThemeMode, getStoredThemeMode, resolveTheme } from "services/theme/themeUtils";

/*
 * SettingsTypes.tsx
 * This file contains type definitions and interfaces of 
 * settings states and fields.
 */ 

// Add user based preferences here:
export interface UserPreferenceType {
    first_name: string | null;
    last_name: string | null;
    profile_picture: string | null;
    timezone: string;
    user_locale: string;
    language: string;
    default_currency: string | null;
    open_tabs: boolean;
    scheduling_view: string | null,
    scheduling_default_view_period: string | null,
    scheduling_direction: string | null,
    scheduling_display_mode: string | null,
    scheduling_color_mode: string | null,
    scheduling_auto_open_job_inbox: boolean | null,
    scheduling_job_inbox_filters: {
        job_types: number[];
        labels: number[];
        conditions: string[];
    } | null;
    scheduling_show_profile_pictures: boolean | null,
    job_photos_display_mode: 'grid' | 'list' | null,
    theme: ThemeMode,
}

export const createUserPreferenceObject = (): UserPreferenceType => ({
    first_name: null,
    last_name: null,
    profile_picture: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    user_locale: getNavigatorLanguageOrFallback(navigator.language, 'locale'),
    language: getNavigatorLanguageOrFallback(navigator.language, 'language'),
    default_currency: null,
    open_tabs: true,
    scheduling_view: null,
    scheduling_default_view_period: null,
    scheduling_direction: null,
    scheduling_display_mode: null,
    scheduling_color_mode: null,
    scheduling_auto_open_job_inbox: null,
    scheduling_job_inbox_filters: null,
    scheduling_show_profile_pictures: null,
    job_photos_display_mode: null,
    theme: getStoredThemeMode(),
})


// Add environment settings here:
export interface EnvironmentSettingsType {
    company_name: string | null;
    default_language: string;
    default_locale: string;
    default_currency: string | null;
    project_job_method: string | null;
    project_numbering_scheme: string | null,
    project_current_number: number | null,
    project_show_scheduling: boolean,
    job_appointment_method: string | null,
    job_default_billing_type: string | null,
    job_time_registration_mode: string | null,
    office_time_tracking: boolean,
    job_numbering_scheme: string | null,
    job_current_number: number | null,
    job_create_jobs: boolean,
    scheduling_display_start_time: string | null,
    scheduling_display_end_time: string | null,
    scheduling_time_blocks: boolean,
    scheduling_appointment_text: string[] | null,
    scheduling_manual_to_schedule_status: boolean,
    scheduling_show_unassigned_row: boolean,
    scheduling_show_employee_timetable: boolean,
    scheduling_default_appointment_whole_day: boolean,
    scheduling_default_appointment_status: string | null,
    invoice_numbering_scheme: string | null,
    invoice_current_number: number | null,
    invoice_show_tax_blocked_acount: boolean,
    contact_default_entity_type: string | null,
    sms_sender_name: string | null,
    sms_quiet_hours_start: string | null,
    sms_quiet_hours_end: string | null,
    contact_numbering_scheme: string | null,
    contact_current_number: number | null,
    ticket_numbering_scheme: string | null,
    ticket_current_number: number | null,
    ticket_email_prefix: string | null,
    ticket_email_domain: number | null,
    proposal_numbering_scheme: string | null,
    proposal_current_number: number | null,
    proposal_use_orders: boolean,
    order_numbering_scheme: string | null,
    order_current_number: number | null,
    mobile_create_jobs: boolean,
    mobile_create_products: boolean,
    mobile_show_job_storage: boolean,
    mobile_offline_customer_database: boolean,
    mobile_appointment_visibility: string | null,
    mobile_appointment_display_mode: string | null,
    mobile_round_off_hours: string | null,
    mobile_timesheets: boolean,
    mobile_payments: boolean,
    mobile_require_job_description: boolean,
    mobile_require_travel_time: boolean,
    mobile_require_work_time: boolean,
    mobile_require_customer_signature: boolean,
    mobile_require_employee_signature: boolean,
    email_domain_method: string | null,
    email_count: number,
    portal_logo: string | null,
    portal_nav_color: string,
    portal_background_color: string,
    portal_button_color: string,
    portal_font: string,
}

export const createEnvironmentSettingsObject = (): EnvironmentSettingsType => ({
    company_name: null,
    default_language: getNavigatorLanguageOrFallback(navigator.language, 'language'),
    default_locale: getNavigatorLanguageOrFallback(navigator.language, 'locale'),
    default_currency: null,
    project_job_method: null,
    project_numbering_scheme: null,
    project_current_number: null,
    project_show_scheduling: false,
    job_appointment_method: null,
    job_default_billing_type: null,
    job_time_registration_mode: null,
    office_time_tracking: false,
    job_numbering_scheme: null,
    job_current_number: null,
    job_create_jobs: true,
    scheduling_display_start_time: null,
    scheduling_display_end_time: null,
    scheduling_time_blocks: false,
    scheduling_appointment_text: null,
    scheduling_manual_to_schedule_status: false,
    scheduling_show_unassigned_row: true,
    scheduling_show_employee_timetable: false,
    scheduling_default_appointment_whole_day: false,
    scheduling_default_appointment_status: null,
    invoice_numbering_scheme: null,
    invoice_current_number: null,
    invoice_show_tax_blocked_acount: false,
    contact_default_entity_type: null,
    sms_sender_name: null,
    sms_quiet_hours_start: null,
    sms_quiet_hours_end: null,
    contact_numbering_scheme: null,
    ticket_numbering_scheme: null,
    ticket_current_number: null,
    ticket_email_prefix: null,
    ticket_email_domain: null,
    contact_current_number: null,
    proposal_numbering_scheme: null,
    proposal_current_number: null,
    proposal_use_orders: false,
    order_numbering_scheme: null,
    order_current_number: null,
    mobile_create_jobs: true,
    mobile_create_products: true,
    mobile_show_job_storage: false,
    mobile_offline_customer_database: false,
    mobile_appointment_visibility: null,
    mobile_appointment_display_mode: null,
    mobile_round_off_hours: null,
    mobile_timesheets: true,
    mobile_payments: true,
    mobile_require_job_description: false,
    mobile_require_travel_time: false,
    mobile_require_work_time: false,
    mobile_require_customer_signature: false,
    mobile_require_employee_signature: false,
    email_domain_method: null,
    email_count: 0,
    portal_logo: null,
    portal_nav_color: '#ffffff',
    portal_background_color: '#f6f6f6',
    portal_button_color: '#2BBF54',
    portal_font: 'arial',
})


// Define the settings context type
type SettingsContextType = {
    // Keep track of the language setting of the user
    setLanguage: (language: string) => void;

    // Keep track of the locale of the user
    userLocale: string;

    // Keep track of the timezone of the user
    userTimezone: string;

    // Keep track of the default currency
    defaultCurrency: string | null;
    defaultCurrencySymbol: string | null;

    // Hook to get the time format based on the user locale
    getTimeFormat: () => '12h' | '24h',

    // Keep track of the user preferences
    userPreferences: UserPreferenceType;
    setUserPreferences: (userPreferences: UserPreferenceType) => void;

    // Keep track of the interface theme currently shown
    resolvedTheme: ResolvedTheme;

    // Keep track of the environment settings
    environmentSettings: EnvironmentSettingsType;
    setEnvironmentSettings: (environmentSettings: EnvironmentSettingsType) => void;
}

// Create the settings context
export const SettingsContext = createContext<SettingsContextType>({
    setLanguage: () => {},
    userLocale: getNavigatorLanguageOrFallback(navigator.language, 'locale'),
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    getTimeFormat: () => '24h',
    defaultCurrency: null,
    defaultCurrencySymbol: null,
    userPreferences: createUserPreferenceObject(),
    setUserPreferences: () => {},
    resolvedTheme: resolveTheme(getStoredThemeMode()),
    environmentSettings: createEnvironmentSettingsObject(),
    setEnvironmentSettings: () => {},
});

export interface SettingsContextProviderProps {
    children: ReactNode;
}